import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 哲学思辨 - 竖屏「思维碰撞」风格
 *
 * 核心亮点：
 * - 对立概念 A vs B 从两侧碰撞
 * - 天平动画（两端权重变化）
 * - 思维路径 SVG 线条
 * - 名言逐字显现
 * - 极简设计 + 大量留白
 * - 概念融合 → 升华主题
 *
 * 尺寸：1080x1920 竖屏
 */

interface PhilosophyConcept {
  label: string;
  icon: string;
  color: string;
  arguments: string[];
}

interface PhilosophyDialecticTemplateProps {
  question: string;
  conceptA: PhilosophyConcept;
  conceptB: PhilosophyConcept;
  synthesis: string;
  quote: string;
  quoteAuthor: string;
  reflection: string;
}

const DEFAULT_PROPS: PhilosophyDialecticTemplateProps = {
  question: '自由意志 vs 决定论',
  conceptA: {
    label: '自由意志',
    icon: '🕊️',
    color: '#818cf8',
    arguments: ['人有选择的能力', '道德责任的前提', '主观体验的确定性'],
  },
  conceptB: {
    label: '决定论',
    icon: '⚙️',
    color: '#f472b6',
    arguments: ['因果律的普遍性', '神经科学的证据', '可预测的行为模式'],
  },
  synthesis: '相容论：自由意志与因果律并不矛盾——自由不是无因之因，而是按照自己的本性行动',
  quote: '人是被判了自由的徒刑',
  quoteAuthor: '让-保罗·萨特',
  reflection: '真正的哲学问题不是选边站，而是在张力中找到更深层的理解',
};

export const PhilosophyDialecticTemplate: React.FC<Partial<PhilosophyDialecticTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Phases
  const questionProgress = spring({ frame, fps, config: { damping: 20, stiffness: 80 } });

  // Concepts enter
  const conceptAStart = 50;
  const conceptBStart = 70;

  const aEnter = interpolate(frame, [conceptAStart, conceptAStart + 25], [0, 1], { extrapolateRight: 'clamp' });
  const bEnter = interpolate(frame, [conceptBStart, conceptBStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  // Arguments
  const argStart = 110;
  const argInterval = 35;
  const maxArgs = Math.max(p.conceptA.arguments.length, p.conceptB.arguments.length);

  // Balance beam
  const balanceStart = argStart + maxArgs * argInterval + 20;
  const balanceProgress = interpolate(frame, [balanceStart, balanceStart + 40], [0, 1], { extrapolateRight: 'clamp' });
  const balanceTilt = interpolate(Math.sin(frame * 0.03), [-1, 1], [-8, 8]) * balanceProgress;

  // Synthesis
  const synthStart = balanceStart + 60;
  const synthOpacity = interpolate(frame, [synthStart, synthStart + 30], [0, 1], { extrapolateRight: 'clamp' });

  // Quote
  const quoteStart = synthStart + 50;
  const quoteChars = p.quote.length;
  const typedChars = Math.floor(interpolate(frame, [quoteStart, quoteStart + quoteChars * 3], [0, quoteChars], { extrapolateRight: 'clamp' }));

  // Reflection
  const reflStart = quoteStart + quoteChars * 3 + 30;
  const reflOpacity = interpolate(frame, [reflStart, reflStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill
      style={{
        background: '#fafaf8',
        fontFamily: '"Noto Serif SC", "Source Han Serif CN", Georgia, serif',
        color: '#1a1a1a',
        overflow: 'hidden',
      }}
    >
      {/* Minimal texture */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(circle, rgba(0,0,0,0.015) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />

      {/* ========== QUESTION ========== */}
      <div
        style={{
          position: 'absolute',
          top: 100,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: questionProgress,
          transform: `translateY(${(1 - questionProgress) * -12}px)`,
        }}
      >
        <div style={{ fontSize: 34, color: '#999', letterSpacing: '0.2em', marginBottom: 16, fontFamily: '-apple-system, sans-serif' }}>
          PHILOSOPHY · 思辨
        </div>
        <h1 style={{ fontSize: 60, fontWeight: 800, margin: '0 60px', letterSpacing: '0.02em', lineHeight: 1.3 }}>
          {p.question}
        </h1>
      </div>

      {/* ========== CONCEPT A (left) ========== */}
      <div
        style={{
          position: 'absolute',
          top: 310,
          left: 60,
          width: 420,
          opacity: aEnter,
          transform: `translateX(${(1 - aEnter) * -40}px)`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 18,
              background: `${p.conceptA.color}12`,
              border: `2px solid ${p.conceptA.color}30`,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize: 40,
            }}
          >
            {p.conceptA.icon}
          </div>
          <div style={{ fontSize: 40, fontWeight: 800, color: p.conceptA.color }}>{p.conceptA.label}</div>
        </div>
        {p.conceptA.arguments.map((arg, i) => {
          const start = argStart + i * argInterval;
          const opacity = interpolate(frame, [start, start + 20], [0, 1], { extrapolateRight: 'clamp' });
          return (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 14, opacity }}>
              <div style={{ width: 16, height: 16, borderRadius: '50%', background: p.conceptA.color, marginTop: 15, flexShrink: 0, opacity: 0.5 }} />
              <span style={{ fontSize: 32, color: '#444', lineHeight: 1.5 }}>{arg}</span>
            </div>
          );
        })}
      </div>

      {/* ========== CONCEPT B (right) ========== */}
      <div
        style={{
          position: 'absolute',
          top: 310,
          right: 60,
          width: 420,
          opacity: bEnter,
          transform: `translateX(${(1 - bEnter) * 40}px)`,
          textAlign: 'right',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16, justifyContent: 'flex-end' }}>
          <div style={{ fontSize: 40, fontWeight: 800, color: p.conceptB.color }}>{p.conceptB.label}</div>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 18,
              background: `${p.conceptB.color}12`,
              border: `2px solid ${p.conceptB.color}30`,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize: 40,
            }}
          >
            {p.conceptB.icon}
          </div>
        </div>
        {p.conceptB.arguments.map((arg, i) => {
          const start = argStart + i * argInterval;
          const opacity = interpolate(frame, [start, start + 20], [0, 1], { extrapolateRight: 'clamp' });
          return (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 14, opacity, justifyContent: 'flex-end' }}>
              <span style={{ fontSize: 32, color: '#444', lineHeight: 1.5 }}>{arg}</span>
              <div style={{ width: 16, height: 16, borderRadius: '50%', background: p.conceptB.color, marginTop: 15, flexShrink: 0, opacity: 0.5 }} />
            </div>
          );
        })}
      </div>

      {/* VS divider */}
      <div
        style={{
          position: 'absolute',
          top: 380,
          left: '50%',
          transform: 'translateX(-50%)',
          opacity: Math.min(aEnter, bEnter),
        }}
      >
        <div
          style={{
            width: 70,
            height: 70,
            borderRadius: '50%',
            background: '#1a1a1a',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: 38,
            fontWeight: 900,
            color: 'white',
            fontFamily: '-apple-system, sans-serif',
          }}
        >
          VS
        </div>
      </div>

      {/* ========== BALANCE BEAM ========== */}
      <div
        style={{
          position: 'absolute',
          top: 650,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          opacity: balanceProgress,
        }}
      >
        <svg width="960" height="256" viewBox="0 0 600 160">
          {/* Fulcrum triangle */}
          <polygon points="300,160 280,130 320,130" fill="#1a1a1a" opacity="0.15" />

          {/* Beam */}
          <line
            x1="80"
            y1="80"
            x2="520"
            y2="80"
            stroke="#1a1a1a"
            strokeWidth="4"
            strokeLinecap="round"
            opacity="0.2"
            transform={`rotate(${balanceTilt}, 300, 80)`}
          />

          {/* Left pan */}
          <g transform={`rotate(${balanceTilt}, 300, 80)`}>
            <line x1="120" y1="80" x2="120" y2="110" stroke="#1a1a1a" strokeWidth="2" opacity="0.15" />
            <ellipse cx="120" cy="115" rx="50" ry="8" fill={p.conceptA.color} opacity="0.15" />
            <text x="120" y="118" fill={p.conceptA.color} fontSize="24" fontWeight="bold" textAnchor="middle" fontFamily="-apple-system, sans-serif">
              {p.conceptA.label}
            </text>
          </g>

          {/* Right pan */}
          <g transform={`rotate(${balanceTilt}, 300, 80)`}>
            <line x1="480" y1="80" x2="480" y2="110" stroke="#1a1a1a" strokeWidth="2" opacity="0.15" />
            <ellipse cx="480" cy="115" rx="50" ry="8" fill={p.conceptB.color} opacity="0.15" />
            <text x="480" y="118" fill={p.conceptB.color} fontSize="24" fontWeight="bold" textAnchor="middle" fontFamily="-apple-system, sans-serif">
              {p.conceptB.label}
            </text>
          </g>
        </svg>
      </div>

      {/* ========== SYNTHESIS ========== */}
      <div
        style={{
          position: 'absolute',
          top: 970,
          left: 60,
          right: 60,
          opacity: synthOpacity,
        }}
      >
        <div
          style={{
            padding: '24px 28px',
            background: 'linear-gradient(135deg, rgba(129,140,248,0.04), rgba(244,114,182,0.04))',
            border: '1px solid rgba(129,140,248,0.12)',
            borderRadius: 20,
          }}
        >
          <div style={{ fontSize: 36, color: '#818cf8', fontWeight: 600, marginBottom: 10, letterSpacing: '0.1em', fontFamily: '-apple-system, sans-serif' }}>
            ⚡ 融合 · SYNTHESIS
          </div>
          <div style={{ fontSize: 34, lineHeight: 1.6, color: '#2a2a2a' }}>{p.synthesis}</div>
        </div>
      </div>

      {/* ========== QUOTE (typewriter) ========== */}
      <div
        style={{
          position: 'absolute',
          top: 1250,
          left: 80,
          right: 80,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: 40,
            fontWeight: 700,
            lineHeight: 1.5,
            color: '#1a1a1a',
            minHeight: 120,
          }}
        >
          「{p.quote.substring(0, typedChars)}
          {typedChars > 0 && typedChars < quoteChars && (
            <span style={{ opacity: frame % 16 < 10 ? 1 : 0, color: '#818cf8' }}>|</span>
          )}
          {typedChars >= quoteChars && '」'}
        </div>
        <div
          style={{
            fontSize: 36,
            color: '#999',
            marginTop: 16,
            opacity: typedChars >= quoteChars ? 1 : 0,
          }}
        >
          —— {p.quoteAuthor}
        </div>
      </div>

      {/* Reflection */}
      <div
        style={{
          position: 'absolute',
          bottom: 120,
          left: 10,
          right: 10,
          textAlign: 'center',
          opacity: reflOpacity,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '5px 32px',
            background: '#1a1a1a',
            borderRadius: 50,
            color: 'white',
            fontSize: 30,
            fontWeight: 600,
            fontFamily: '-apple-system, "PingFang SC", sans-serif',
          }}
        >
          💭 {p.reflection}
        </div>
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, reflStart + 40], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 3,
          background: `linear-gradient(90deg, ${p.conceptA.color}, ${p.conceptB.color})`,
        }}
      />
    </AbsoluteFill>
  );
};
